import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, TestTube, Eye, EyeOff, Copy, Check, ChevronDown, ChevronRight, Activity, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const WEBHOOK_EVENTS = [
  { value: "order.paid", label: "Pedido Pago", desc: "Disparado assim que a transação é aprovada e confirmada." },
  { value: "order.cancelled", label: "Pedido Cancelado", desc: "Disparado quando a compra expira ou é cancelada pelo comprador/sistema." },
  { value: "order.refunded", label: "Pedido Reembolsado", desc: "Disparado no momento em que um reembolso total ou parcial é efetuado." },
  { value: "ticket.checked_in", label: "Check-in Realizado", desc: "Disparado quando o QR code de um ingresso é validado na portaria." },
  { value: "ticket.transferred", label: "Ingresso Transferido", desc: "Disparado quando um participante transfere seu ingresso para outra pessoa." },
  { value: "event.published", label: "Evento Publicado", desc: "Disparado quando um rascunho de evento é colocado no ar e ativado." },
  { value: "event.cancelled", label: "Evento Cancelado", desc: "Disparado quando a realização de um evento é formalmente suspensa." },
];

const getPayloadExample = (selectedEvents: string[]) => {
  const eventsToGenerate = selectedEvents.length > 0 ? selectedEvents : ["order.paid"];
  
  const examples = eventsToGenerate.map(event => {
    const base = {
      event,
      created_at: new Date().toISOString(),
    };

    switch (event) {
      case "order.paid":
        return {
          ...base,
          data: {
            order_id: "ord_8f2a9c4d",
            event_id: "evt_7d8e9f",
            amount: 150.00,
            buyer_email: "cliente@email.com",
            tickets: [
              { id: "tk_c4b3a210", tier: "Pista Premium", price: 150.00 }
            ]
          }
        };
      case "order.cancelled":
        return {
          ...base,
          data: {
            order_id: "ord_8f2a9c4d",
            event_id: "evt_7d8e9f",
            amount: 150.00,
            reason: "Pagamento via Pix não concluído dentro do prazo de 15 minutos."
          }
        };
      case "order.refunded":
        return {
          ...base,
          data: {
            order_id: "ord_8f2a9c4d",
            event_id: "evt_7d8e9f",
            amount_refunded: 150.00,
            reason: "Solicitação de cancelamento pelo cliente."
          }
        };
      case "ticket.checked_in":
        return {
          ...base,
          data: {
            ticket_id: "tk_c4b3a210",
            event_id: "evt_7d8e9f",
            checked_in_at: new Date().toISOString(),
            scanned_by: "usr_5c4b3a21",
            device_id: "iphone-15-pro"
          }
        };
      case "ticket.transferred":
        return {
          ...base,
          data: {
            ticket_id: "tk_c4b3a210",
            event_id: "evt_7d8e9f",
            from_email: "antigo_dono@email.com",
            to_email: "novo_dono@email.com",
            transferred_at: new Date().toISOString()
          }
        };
      case "event.published":
        return {
          ...base,
          data: {
            event_id: "evt_7d8e9f",
            title: "Festival da Cerveja 2026",
            slug: "festival-cerveja-2026",
            start_date: "2026-10-15T18:00:00Z"
          }
        };
      case "event.cancelled":
        return {
          ...base,
          data: {
            event_id: "evt_7d8e9f",
            title: "Festival da Cerveja 2026",
            reason: "Condições climáticas extremas previstas para a data."
          }
        };
      default:
        return base;
    }
  });

  if (examples.length === 1) {
    return JSON.stringify(examples[0], null, 2);
  }
  return JSON.stringify(examples, null, 2);
};

export function WebhooksManager() {
  const { t } = useTranslation("producerOrg");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showDeliveries, setShowDeliveries] = useState<string | null>(null);
  const [expandedDelivery, setExpandedDelivery] = useState<string | null>(null);
  const [form, setForm] = useState({ url: "", events: [] as string[] });
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [copiedSecretId, setCopiedSecretId] = useState<string | null>(null);

  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ["producer-webhooks", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhooks")
        .select("*")
        .eq("producer_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: deliveries = [], isLoading: isLoadingDeliveries } = useQuery({
    queryKey: ["webhook-deliveries", showDeliveries],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("webhook_deliveries")
        .select("*")
        .eq("webhook_id", showDeliveries!)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
    enabled: !!showDeliveries,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const secret = `whsec_${crypto.randomUUID().replace(/-/g, "")}`;
      const { error } = await supabase.from("webhooks").insert({
        producer_id: user!.id,
        url: form.url,
        secret,
        events: form.events,
        is_active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-webhooks"] });
      setShowCreate(false);
      setForm({ url: "", events: [] });
      toast({ title: t("webhooks.toasts.created"), description: t("webhooks.toasts.createdDescription") });
    },
    onError: (err: any) => toast({ title: t("webhooks.toasts.createError"), description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("webhooks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-webhooks"] });
      toast({ title: t("webhooks.toasts.deleted"), description: t("webhooks.toasts.deletedDescription") });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from("webhooks").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-webhooks"] });
      toast({ title: t("webhooks.toasts.updated"), description: t("webhooks.toasts.updatedDescription") });
    },
  });

  const testMutation = useMutation({
    mutationFn: async (webhook: any) => {
      const testPayload = {
        event: "test.ping",
        data: {
          message: "Teste de comunicação do webhook TicketHall",
          timestamp: new Date().toISOString(),
          producer_id: user?.id,
          environment: import.meta.env.MODE,
        }
      };

      const res = await supabase.functions.invoke("dispatch-webhook", {
        body: {
          webhook_id: webhook.id,
          event_type: "test.ping",
          payload: testPayload,
        },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["producer-webhooks"] });
      toast({ title: t("webhooks.toasts.pingSent"), description: t("webhooks.toasts.pingSentDescription") });
    },
    onError: (err: any) => toast({ title: t("webhooks.toasts.pingError"), description: err.message, variant: "destructive" }),
  });

  const handleCopySecret = (secret: string, id: string) => {
    navigator.clipboard.writeText(secret);
    setCopiedSecretId(id);
    toast({ title: t("webhooks.toasts.secretCopied"), description: t("webhooks.toasts.secretCopiedDescription") });
    setTimeout(() => setCopiedSecretId(null), 2000);
  };

  const toggleEvent = (event: string) => {
    setForm((p) => ({
      ...p,
      events: p.events.includes(event) ? p.events.filter((e) => e !== event) : [...p.events, event],
    }));
  };

  const isUrlValid = (url: string) => {
    return /^https?:\/\/[^\s$.?#].[^\s]*$/i.test(url);
  };

  return (
    <div className="space-y-6">
      {/* Introduction Banner */}
      <div className="rounded-2xl border border-border/70 bg-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h3 className="font-display font-bold text-base text-foreground">{t("webhooks.bannerTitle")}</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {t("webhooks.bannerDescription")}
        </p>

      </div>

      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">{t("webhooks.sectionTitle")}</h4>
          <p className="text-xs text-muted-foreground">{t("webhooks.sectionSubtitle")}</p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowCreate(!showCreate)}
          className="rounded-xl font-semibold gap-1.5 h-9 bg-primary hover:bg-primary/95 text-white"
        >
          <Plus className={cn("h-4 w-4 transition-transform duration-200", showCreate && "rotate-45")} />
          {showCreate ? t("webhooks.cancelButton") : t("webhooks.newWebhookButton")}
        </Button>
      </div>

      {/* Inline Create Form */}
      {showCreate && (
        <Card className="border border-border/70 bg-card rounded-2xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-top-3 duration-200">
          <CardHeader className="pb-4 border-b border-border/50">
            <CardTitle className="text-base font-bold">Novo Webhook</CardTitle>
            <CardDescription className="text-xs">
              Configure uma URL de endpoint segura para receber eventos e payloads em tempo real.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-5">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-6">
              {/* Left Column: Form fields */}
              <div className="space-y-5">
                {/* URL input */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-foreground">URL do Endpoint de Destino</Label>
                  <Input
                    value={form.url}
                    onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                    placeholder="https://sua-api.com/webhooks/tickethall"
                    className="h-11 rounded-xl border-border/50 bg-background/50 focus-visible:bg-background transition-all"
                  />
                  <p className="text-[10px] text-muted-foreground leading-normal">
                    Insira uma URL pública que aceite requisições HTTP POST com cabeçalho <code className="font-mono">application/json</code>. Preferencialmente utilize conexões criptografadas (https).
                  </p>
                </div>

                {/* Event checklist */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">Gatilhos de Eventos (Selecione pelo menos um)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {WEBHOOK_EVENTS.map((ev) => {
                      const isChecked = form.events.includes(ev.value);
                      return (
                        <div
                          key={ev.value}
                          onClick={() => toggleEvent(ev.value)}
                          className={cn(
                            "flex items-start gap-3 p-3 cursor-pointer rounded-xl border border-border/50 hover:bg-muted/50 transition-all",
                            isChecked ? "bg-primary/5 border-primary/30" : "bg-muted/10"
                          )}
                        >
                          <div className="pt-0.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}} // toggled via click
                              className="rounded text-primary focus:ring-primary h-3.5 w-3.5 cursor-pointer accent-primary"
                            />
                          </div>
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-foreground flex items-center gap-1.5 flex-wrap">
                              {ev.label}
                            </p>
                            <p className="text-[10px] text-muted-foreground leading-normal">{ev.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Payload Example */}
              <div className="space-y-2 lg:border-l lg:pl-6 border-border/50">
                <Label className="text-sm font-semibold text-foreground">
                  Exemplo de Payload JSON
                </Label>
                <div className="rounded-xl border border-border/50 bg-muted/20 p-3.5 space-y-2">
                  <p className="text-[10px] text-muted-foreground leading-normal">
                    Formato do objeto enviado para os eventos selecionados:
                  </p>
                  <pre className="text-[10px] font-mono bg-background border border-border/40 p-2.5 rounded-lg overflow-x-auto text-foreground/80 max-h-[300px]">
                    {getPayloadExample(form.events)}
                  </pre>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-border/50">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} className="rounded-lg">
                Cancelar
              </Button>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={!form.url || !isUrlValid(form.url) || form.events.length === 0 || createMutation.isPending}
                size="sm"
                className="rounded-lg bg-primary hover:bg-primary/95 text-white font-semibold"
              >
                Salvar Webhook
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-2xl" />
          ))}
        </div>
      ) : webhooks.length === 0 ? (
        <Card className="border border-dashed border-border/80">
          <CardContent className="py-12 text-center space-y-2">
            <ShieldAlert className="h-8 w-8 text-muted-foreground/60 mx-auto" />
            <p className="text-sm text-muted-foreground font-medium">Nenhum endpoint configurado ainda.</p>
            <p className="text-xs text-muted-foreground/80 max-w-sm mx-auto">Adicione um webhook para enviar dados em tempo real sempre que novas vendas forem geradas.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((wh: any) => (
            <Card key={wh.id} className="border border-border/70 bg-card overflow-hidden shadow-sm hover:border-border transition-all">
              <CardContent className="p-5 space-y-4">
                {/* Header info */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-foreground truncate max-w-[450px]">
                        {wh.url}
                      </span>
                      <Badge variant={wh.is_active ? "default" : "secondary"} className={wh.is_active ? "bg-green-600/10 text-green-500 hover:bg-green-600/15" : "bg-muted text-muted-foreground"}>
                        {wh.is_active ? "Ativo" : "Pausado"}
                      </Badge>
                    </div>
                    {/* Event badges */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {wh.events?.map((ev: string) => (
                        <Badge key={ev} variant="outline" className="text-[10px] bg-background border-border/50 py-0 px-2 font-mono">
                          {ev}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Switch
                      checked={wh.is_active}
                      onCheckedChange={(v) => toggleMutation.mutate({ id: wh.id, is_active: v })}
                    />
                  </div>
                </div>

                {/* Signing secret key container */}
                <div className="p-3 bg-muted/40 border border-border/30 rounded-xl flex items-center justify-between text-xs gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-muted-foreground shrink-0">Chave Secreta (Assinatura):</span>
                    <span className="font-mono bg-background px-2 py-0.5 rounded border border-border/40 truncate text-foreground/80">
                      {showSecret[wh.id] ? wh.secret : "••••••••••••••••••••••••••••••••••••"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowSecret((p) => ({ ...p, [wh.id]: !p[wh.id] }))}
                    >
                      {showSecret[wh.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => handleCopySecret(wh.secret, wh.id)}
                    >
                      {copiedSecretId === wh.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>

                {/* Action buttons toolbar */}
                <div className="flex items-center justify-between pt-1 border-t border-border/45">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg h-8 text-xs font-semibold gap-1.5 hover:bg-muted/50"
                      onClick={() => testMutation.mutate(wh)}
                      disabled={testMutation.isPending}
                    >
                      <TestTube className="h-3.5 w-3.5 text-muted-foreground" /> Testar Conexão
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg h-8 text-xs font-semibold hover:bg-muted/50"
                      onClick={() => setShowDeliveries(wh.id)}
                    >
                      Histórico de Envios
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive rounded-lg px-2.5"
                    onClick={() => {
                      if (confirm("Tem certeza que deseja deletar este webhook permanentemente?")) {
                        deleteMutation.mutate(wh.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}



      {/* Deliveries dialog */}
      <Dialog open={!!showDeliveries} onOpenChange={() => { setShowDeliveries(null); setExpandedDelivery(null); }}>
        <DialogContent mobilePresentation="fullscreen" className="max-w-2xl max-h-[85vh] overflow-y-auto p-6 rounded-2xl">
          <DialogHeader className="pb-3 border-b border-border/50">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              Histórico de Entregas
            </DialogTitle>
            <DialogDescription className="text-xs">
              Lista dos últimos 20 envios de eventos disparados para este webhook.
            </DialogDescription>
          </DialogHeader>

          {isLoadingDeliveries ? (
            <div className="space-y-3 py-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : deliveries.length === 0 ? (
            <div className="py-12 text-center space-y-2">
              <CheckCircle2 className="h-8 w-8 text-green-500/60 mx-auto" />
              <p className="text-sm font-semibold text-foreground">Tudo limpo por aqui</p>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">Nenhuma entrega de evento foi registrada nas últimas horas para este endpoint.</p>
            </div>
          ) : (
            <div className="space-y-3 py-3">
              {deliveries.map((d: any) => {
                const isSuccess = d.response_status && d.response_status >= 200 && d.response_status < 300;
                const isExpanded = expandedDelivery === d.id;

                return (
                  <div
                    key={d.id}
                    className={`rounded-xl border transition-all ${isExpanded ? "border-border bg-muted/10" : "border-border/60 hover:bg-muted/5"}`}
                  >
                    {/* Log Main Header */}
                    <div
                      className="p-3.5 flex items-center justify-between gap-3 cursor-pointer"
                      onClick={() => setExpandedDelivery(isExpanded ? null : d.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isSuccess ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-semibold font-mono text-foreground truncate">
                            {d.event_type}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {new Date(d.created_at).toLocaleString("pt-BR")} · {d.attempts} tentativa(s)
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant={isSuccess ? "outline" : "destructive"}
                          className={`text-xs font-mono py-0.5 px-2 font-bold ${
                            isSuccess
                              ? "bg-green-500/5 text-green-600 border-green-500/20"
                              : "bg-destructive/5 text-destructive border-destructive/20"
                          }`}
                        >
                          HTTP {d.response_status || "ERR"}
                        </Badge>
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </div>

                    {/* Collapsible JSON payload inspect view */}
                    {isExpanded && (
                      <div className="p-4 border-t border-border/50 bg-background/50 rounded-b-xl space-y-3 text-xs">
                        {/* Payload */}
                        <div className="space-y-1">
                          <p className="font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Payload Enviado (POST JSON)</p>
                          <pre className="p-3 bg-muted/60 border border-border/40 rounded-lg text-[10px] font-mono overflow-auto max-h-40 max-w-full text-foreground/90">
                            {JSON.stringify(d.payload, null, 2)}
                          </pre>
                        </div>

                        {/* Response body */}
                        <div className="space-y-1">
                          <p className="font-semibold text-muted-foreground uppercase text-[9px] tracking-wider">Corpo da Resposta do Servidor</p>
                          {d.response_body ? (
                            <pre className="p-3 bg-muted/60 border border-border/40 rounded-lg text-[10px] font-mono overflow-auto max-h-32 max-w-full text-foreground/90">
                              {d.response_body}
                            </pre>
                          ) : (
                            <p className="text-[10px] italic text-muted-foreground pl-1">Sem resposta ou falha na conexão de rede.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
